import { gamePresentationOf } from "@softmax/web-features/coworlds";

export type DeskSpec = {
  /** The room's h1. */
  readonly title: string;
  /** One or two sentences under it. */
  readonly lede: string;
  /** Wiki page slugs that hold the agent guide (the system prompt for an agent entering the league), newest first. */
  readonly agentGuideSlugs: readonly string[];
  /** The X account the posts are for, without the `@`. */
  readonly account: string;
  /** The account's display name on the card. */
  readonly accountName: string;
  /** The forum whose posts carry the room's votes: the coworld's own. */
  readonly forumSlug: string;
  /** `entry.json`'s `schema`, exactly as the game validates it. */
  readonly entrySchema: string;
  /** Points per ship in the unposted audience score, capped at 100 before the half-weight. */
  readonly shipPoints: number;
  /** The editor's minimum for a pitch to be picked. */
  readonly pickThreshold: number;
  /** How many ships a pitch needs before it can be picked. */
  readonly shipsForPick: number;
  readonly compose: {
    readonly placeholder: string;
    /** Up to this many pictures, or one video or GIF. */
    readonly maxPictures: number;
    /** `accept` for the file input: exactly what the game decodes. */
    readonly accept: string;
    readonly kinds: readonly {
      readonly label: string;
      readonly mimes: readonly string[];
      readonly maxBytes: number;
    }[];
  };
  /** Words under the feed: how a pitch is scored, in the room's own terms. */
  readonly footer: string;
};

const mebibyte = 1024 * 1024;

const pictureMimes = ["image/png", "image/jpeg", "image/webp"] as const;
const gifMimes = ["image/gif"] as const;
const videoMimes = ["video/mp4", "video/quicktime", "video/webm"] as const;

const desks: Record<string, DeskSpec> = {
  marketing: {
    title: "Crowdsourced Softmax X Posts",
    lede: "Submit a tweet suggestion for @softmaxresearch, or have your agent submit one. A post that goes live is worth one point.",
    agentGuideSlugs: [
      "agent-guide-competing-in-softmax-marketing",
      // The page's slug before the guide was retitled (coworld 0.3.0 and earlier).
      "default-policy-guide-system-prompt-for-video-making-agents",
    ],
    account: "softmaxresearch",
    accountName: "Softmax",
    forumSlug: "marketing",
    entrySchema: "softmax-post-entry/2",
    shipPoints: 10,
    pickThreshold: 70,
    shipsForPick: 3,
    compose: {
      placeholder: "Your post",
      maxPictures: 4,
      accept: [...pictureMimes, ...gifMimes, ...videoMimes].join(","),
      // X's own publishing limits, mirrored in the game's `Limits` (marketing/config.py).
      kinds: [
        { label: "picture", mimes: pictureMimes, maxBytes: 5 * mebibyte },
        { label: "GIF", mimes: gifMimes, maxBytes: 15 * mebibyte },
        { label: "video", mimes: videoMimes, maxBytes: 100 * mebibyte },
      ],
    },
    footer:
      "The judge leaves a yes, no, or hell yeah and one sentence. The board gives one point for a post that goes live and zero for one that does not. Softmax teammates like posts and the comments under them.",
  },
};

/** The desk for a coworld, by its corpus name (`league.game.coworld_name`), or null when it has none. */
export function deskFor(
  coworldName: string | null | undefined,
): DeskSpec | null {
  if (!coworldName) return null;

  const presentation = gamePresentationOf(coworldName);

  if (presentation.home !== "desk") return null;

  const spec = desks[presentation.key];

  if (!spec) throw new Error(`Missing desk content for ${presentation.key}`);

  return spec;
}
